import graphql__5ffa05745ded39d7bd0e from "./__generated__/ConfigurationBankAccountsViewQuery.graphql.ts";import * as React from "react";
import {startTransition, useState} from "react";
import {useOutletContext} from "react-router";
import {graphql} from "react-relay";
import {usePreloadedQuery, useQueryLoader} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {faPencil, faPlusCircle} from "@fortawesome/pro-regular-svg-icons";
import Panel from "./presentational/Panel";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import ActionButton from "./presentational/button/ActionButton";
import EditBankAccountDialog from "./dialog/EditBankAccountDialog";
import {formatAmount} from "../helper/revenueFormat";
import {formatDay} from "../helper/dateFormat";
import ConfigurationBankAccountsViewQueryNode from "./__generated__/ConfigurationBankAccountsViewQuery.graphql";
import type {ConfigurationBankAccountsViewQuery as ConfigurationBankAccountsViewQueryType} from "./__generated__/ConfigurationBankAccountsViewQuery.graphql";

const configurationBankAccountsViewQuery = graphql__5ffa05745ded39d7bd0e;

type ConfigurationOutletContext = {
    configurationBankAccountsViewQuery: any
};

export type BankAccountRow = {
    readonly id: string
    readonly bankAccountIdentifier: string
    readonly name: string
    readonly anchorDate: any
    readonly anchorBalance: number
};

type OpenDialog =
    | {type: 'create'}
    | {type: 'edit', bankAccount: BankAccountRow}
    | null;

/**
 * Lists the bank accounts with the known balance the computed balance starts from. Statements are
 * uploaded in the forecast.
 */
const ConfigurationBankAccountsView: React.FC = () => {
    const intl = useIntl();
    const {configurationBankAccountsViewQuery: preloadedQuery} = useOutletContext<ConfigurationOutletContext>();
    const [queryRef, loadBankAccountsQuery] = useQueryLoader<ConfigurationBankAccountsViewQueryType>(ConfigurationBankAccountsViewQueryNode, preloadedQuery);
    const data = usePreloadedQuery<ConfigurationBankAccountsViewQueryType>(configurationBankAccountsViewQuery, queryRef ?? preloadedQuery);
    const [openDialog, setOpenDialog] = useState<OpenDialog>(null);

    const bankAccounts = data.bankAccounts;

    const closeDialog = (changed: boolean) => {
        setOpenDialog(null);
        if (changed) {
            startTransition(() => {
                loadBankAccountsQuery({}, {fetchPolicy: 'network-only'});
            });
        }
    };

    return (
        <>
            <Panel
                headline={intl.formatMessage({id: 'view.configurationBankAccounts.headline'})}
                buttons={
                    <ActionButton
                        icon={faPlusCircle}
                        title={intl.formatMessage({id: 'view.configurationBankAccounts.createButton'})}
                        onClick={() => setOpenDialog({type: 'create'})}
                    />
                }
                fullWidthChildren={true}
            >
                {bankAccounts.length === 0 ? (
                    <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.configurationBankAccounts.empty'})}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader className="pl-4 sm:pl-6 pr-3">{intl.formatMessage({id: 'view.configurationBankAccounts.col.name'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.configurationBankAccounts.col.anchor'})}</TableHeader>
                                    <TableHeader className="pl-3 pr-4 sm:pr-6"/>
                                </tr>
                            </thead>
                            <tbody>
                                {bankAccounts.map((bankAccount, index) => (
                                    <tr key={bankAccount.id} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                        <TableData className="pl-4 sm:pl-6 pr-3">
                                            <span className="font-medium text-gray-900">{bankAccount.name}</span>
                                        </TableData>
                                        <TableData className="px-3 whitespace-nowrap">
                                            {intl.formatMessage(
                                                {id: 'view.configurationBankAccounts.anchor'},
                                                {date: formatDay(intl, bankAccount.anchorDate), amount: formatAmount(bankAccount.anchorBalance)}
                                            )}
                                        </TableData>
                                        <TableData className="pl-3 pr-4 sm:pr-6 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                            <ActionButton
                                                icon={faPencil}
                                                title={intl.formatMessage({id: 'view.configurationBankAccounts.action.edit'})}
                                                additionalClassname="invisible group-hover:visible"
                                                onClick={() => setOpenDialog({type: 'edit', bankAccount})}
                                            />
                                        </TableData>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Panel>

            {openDialog?.type === 'create' && (
                <EditBankAccountDialog onClose={closeDialog}/>
            )}
            {openDialog?.type === 'edit' && (
                <EditBankAccountDialog bankAccount={openDialog.bankAccount} onClose={closeDialog}/>
            )}
        </>
    );
};

export default ConfigurationBankAccountsView;
